import React, { useEffect, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Translate } from 'next-translate';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { TopToast } from '@/customTypes/chatStory';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import { getToastTime } from '@common/utils';

const TopToastItem = ({ t, toast }: { t: Translate; toast: TopToast }) => {
  const { setTopToasts } = useChatPlayContext();
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    showToast();
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, []);

  const showToast = () => {
    const msec = getToastTime(
      `${toast?.displayName || toast.name || ''} ${toast?.description || ''}`,
    );

    timerRef.current = setTimeout(() => {
      setTopToasts((prev) => prev?.filter((item) => item.id !== toast?.id));
      timerRef.current && clearTimeout(timerRef.current);
    }, msec);
  };

  return (
    <div css={toastItem}>
      {toast?.imageLink && (
        <img className="toast-img" src={toast?.imageLink} alt="Toast image" />
      )}
      <div className="toast-text">
        {`[${toast?.displayName || toast.name}] `}
        {toast.type === 'item'
          ? toast?.itemCount === undefined
            ? t('chat_toast_description_item_gained')
            : toast.itemCount > 0
            ? t('chat_toast_title_get_item_with_value_web', {
                value: Math.abs(toast.itemCount),
              })
            : t('chat_toast_title_use_item_with_value_web', {
                value: Math.abs(toast.itemCount),
              })
          : t('chat_toast_title_get_achievement')}
      </div>
    </div>
  );
};

const TopToastItemList = () => {
  const { t } = useTranslation();
  const { topToasts } = useChatPlayContext();

  if (!topToasts) {
    return null;
  }

  return (
    <div css={toastContainer}>
      {topToasts.map((item) => (
        <TopToastItem t={t} key={item.id} toast={item} />
      ))}
    </div>
  );
};

const toastContainer = css`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  gap: 1em;
  top: 1.25em;
`;

const toastItem = css`
  max-width: 450px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray300};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  border: none;
  padding: 12px 20px 12px 12px;

  .toast-text {
    font-size: 1em;
    font-weight: 400;
    display: flex;
    text-align: left;
  }

  .toast-title {
    white-space: nowrap;
    margin-right: 0.2em;
    font-weight: 700;
  }

  .toast-img {
    width: 24px;
    height: 24px;
    margin-right: 0.75em;
  }

  @media screen and (max-width: ${theme.devices.mobile}) {
    max-width: 335px;
  }
`;

export default TopToastItemList;
